import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { useContentId } from '@/components/SlugGate';
import SEOHead from '@/components/SEOHead';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  BookOpen, 
  Lock, 
  Unlock,
  Play, 
  Clock, 
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Crown,
  Download,
  FileText
} from 'lucide-react';
import { extractErrorMessage } from '@/lib/api-errors';

function EpisodeCard({ episode, index, isLocked, onRead }) {
  return (
    <Card 
      className={`bg-surface border-border transition-all ${
        isLocked ? 'opacity-75' : 'cursor-pointer hover:border-ruby/50'
      }`}
      onClick={() => !isLocked && onRead(episode.story_id)}
      data-testid={`episode-card-${episode.episode_number}`}
    >
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          {/* Episode Number */}
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold ${
            isLocked ? 'bg-white/5 text-muted-foreground' : 'bg-ruby/20 text-ruby'
          }`}>
            {episode.episode_number}
          </div>
          
          {/* Episode Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className={`font-medium truncate ${isLocked ? 'text-muted-foreground' : 'text-foreground'}`}>
                {episode.title}
              </h3>
              {isLocked ? (
                <Lock className="w-4 h-4 text-muted-foreground shrink-0" />
              ) : (
                <Unlock className="w-4 h-4 text-green-400 shrink-0" />
              )}
            </div>
            <p className="text-sm text-muted-foreground line-clamp-1">
              {episode.excerpt}
            </p>
          </div>
          
          {/* Reading Time */}
          <div className="hidden sm:flex items-center gap-1 text-sm text-muted-foreground shrink-0">
            <Clock className="w-4 h-4" />
            {episode.reading_time} min
          </div>
          
          {/* Action */}
          {!isLocked && (
            <Button 
              size="sm" 
              className="bg-ruby hover:bg-ruby-light shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                onRead(episode.story_id);
              }}
            >
              <Play className="w-4 h-4 mr-1" />
              Ler
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SeriesDetail({
  seriesType = 'story',
  listRoute = '/series',
  listLabel = 'Voltar às Séries',
  badgeLabel = 'Premium',
} = {}) {
  const id = useContentId();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSeries();
  }, [id]);

  const loadSeries = async () => {
    try {
      const res = await api.get(`/series/${id}`);
      setSeries(res.data);
    } catch (error) {
      toast.error('Série não encontrada');
      navigate('/series');
    } finally {
      setLoading(false);
    }
  };

  const handleGoPremium = () => {
    if (!user) {
      toast.error('Precisa de estar autenticado');
      navigate('/login');
      return;
    }
    // Per-series purchases were retired. Direct users to the unified
    // Premium subscription page.
    navigate('/premium');
  };

  const handleReadEpisode = (storyId) => {
    navigate(`/read/${storyId}`);
  };

  const handleDownloadPdf = async () => {
    if (!user) {
      toast.error('Faz login para descarregar');
      navigate('/login');
      return;
    }
    if (!series.is_purchased && !user.is_admin && !user.is_premium) {
      toast.info('Subscreve Premium para descarregar este PDF');
      navigate('/premium');
      return;
    }
    try {
      const res = await api.get(`/series/${id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${series.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Erro ao descarregar PDF'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-ruby" />
        </div>
      </div>
    );
  }

  if (!series) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="pt-20 sm:pt-24 pb-20 sm:pb-12 px-4 sm:px-6">
          <div className="max-w-xl mx-auto text-center" data-testid="series-not-found">
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-ruby/15 border border-ruby/30 flex items-center justify-center">
              <BookOpen className="w-7 h-7 text-ruby" />
            </div>
            <h1 className="font-brand text-2xl font-bold text-foreground mb-2">
              Série não encontrada
            </h1>
            <p className="text-muted-foreground text-sm mb-6">
              Esta série pode ter sido removida ou ainda não está publicada.
            </p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button onClick={() => navigate('/series')} className="bg-ruby hover:bg-ruby-light">
                Ver todas as séries
              </Button>
              <Button onClick={() => navigate('/home')} variant="outline" className="border-border">
                Ir para o início
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <SEOHead title={series.title} description={series.description} image={series.cover_image} type="website" />
      
      <main className="pt-20 pb-12">
        {/* Hero with Cover */}
        <div className="relative">
          {/* Background Image */}
          <div className="absolute inset-0 h-80">
            {series.cover_image ? (
              <img 
                src={series.cover_image} 
                alt={series.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-ruby/30 to-ruby/10" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/80 to-transparent" />
          </div>
          
          {/* Content */}
          <div className="relative max-w-6xl mx-auto px-6 pt-8">
            <Button
              variant="ghost"
              onClick={() => navigate(listRoute)}
              className="mb-6 text-foreground/80 hover:text-foreground"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {listLabel}
            </Button>
            
            <div className="flex flex-col md:flex-row gap-8 pb-8">
              {/* Cover Image */}
              <div className="shrink-0">
                <div className="w-full md:w-64 aspect-[3/4] rounded-lg overflow-hidden shadow-2xl">
                  {series.cover_image ? (
                    <img 
                      src={series.cover_image} 
                      alt={series.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-ruby/50 to-ruby/20 flex items-center justify-center">
                      <BookOpen className="w-20 h-20 text-ruby/50" />
                    </div>
                  )}
                </div>
              </div>
              
              {/* Series Info */}
              <div className="flex-1 pt-20 md:pt-32">
                <div className="flex items-center gap-3 mb-4">
                  <Badge variant="outline" className="border-ruby/50 text-ruby">
                    <BookOpen className="w-3 h-3 mr-1" />
                    {badgeLabel}
                  </Badge>
                  <Badge variant="outline" className="border-border">
                    {series.episodes?.length || 0} {seriesType === 'guide' ? 'capítulos' : 'episódios'}
                  </Badge>
                  {series.pdf_url && (
                    <Badge variant="outline" className="border-gold/50 text-gold">
                      <FileText className="w-3 h-3 mr-1" />
                      PDF
                    </Badge>
                  )}
                </div>
                
                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4" data-testid="series-title">
                  {series.title}
                </h1>
                
                <p className="text-lg text-muted-foreground mb-6 max-w-2xl">
                  {series.description}
                </p>
                
                {/* Access section */}
                <div className="flex flex-wrap items-center gap-4">
                  {series.is_purchased ? (
                    <div className="flex items-center gap-2 bg-green-500/20 px-4 py-3 rounded-lg">
                      <CheckCircle2 className="w-5 h-5 text-green-400" />
                      <span className="text-green-400 font-medium">
                        {user?.is_premium && !series.is_legacy_purchase
                          ? 'Desbloqueado com Premium'
                          : (seriesType === 'guide' ? 'Guia Desbloqueado' : 'Série Desbloqueada')}
                      </span>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 bg-ruby/20 px-4 py-3 rounded-lg">
                        <Crown className="w-5 h-5 text-ruby" />
                        <span className="text-sm md:text-base font-medium text-foreground">
                          Incluído no Premium
                        </span>
                      </div>
                      <Button
                        size="lg"
                        onClick={handleGoPremium}
                        className="bg-ruby hover:bg-ruby-light px-8"
                        data-testid="series-go-premium-btn"
                      >
                        <Crown className="w-5 h-5 mr-2" />
                        Desbloquear com Premium
                      </Button>
                    </>
                  )}
                  {/* PDF download visible once unlocked (or for admins) */}
                  {series.pdf_url && (series.is_purchased || user?.is_admin) && (
                    <Button
                      size="lg"
                      variant="outline"
                      onClick={handleDownloadPdf}
                      className="border-gold/50 text-gold hover:bg-gold/10"
                      data-testid="download-series-pdf"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Descarregar PDF
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Episodes List */}
        <div className="max-w-4xl mx-auto px-6 mt-8">
          <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
            <Play className="w-6 h-6 text-ruby" />
            {seriesType === 'guide' ? 'Capítulos' : 'Episódios'}
          </h2>
          
          {series.episodes && series.episodes.length > 0 ? (
            <div className="space-y-3">
              {series.episodes.map((episode, index) => (
                <EpisodeCard
                  key={episode.story_id}
                  episode={episode}
                  index={index}
                  isLocked={episode.is_locked !== undefined ? episode.is_locked : !series.is_purchased}
                  onRead={handleReadEpisode}
                />
              ))}
            </div>
          ) : (
            <Card className="bg-surface border-border">
              <CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  Esta série ainda não tem episódios. Volta em breve!
                </p>
              </CardContent>
            </Card>
          )}
          
          {/* Locked Notice */}
          {!series.is_purchased && series.episodes?.length > 0 && (
            <Card className="bg-ruby/10 border-ruby/30 mt-6">
              <CardContent className="py-6">
                <div className="flex items-center gap-4">
                  <Lock className="w-8 h-8 text-ruby shrink-0" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-foreground mb-1">Conteúdo Premium</h3>
                    <p className="text-sm text-muted-foreground">
                      A subscrição Premium dá acesso a <strong>todos os contos, séries, guias Conhece-te</strong> e canais exclusivos da Intimate.pt — sem precisar de comprar cada um separadamente.
                    </p>
                  </div>
                  <Button
                    onClick={handleGoPremium}
                    className="bg-ruby hover:bg-ruby-light shrink-0"
                    data-testid="series-locked-go-premium"
                  >
                    <Crown className="w-4 h-4 mr-2" />
                    Subscrever Premium
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
